import { StarIcon } from "../../../components/icons/StarIcon";
import { DotsMenuIcon } from "../../../components/icons/DotsMenuIcon";
import { CalendarIcon } from "../../../components/icons/CalendarIcon";
import { TagIcon } from "../../../components/icons/TagIcon";
import { FlagIcon } from "../../../components/icons/FlagIcon";

export interface ProjectStats {
  month: number;
  tags: number;
  nextMilestone: string;
}

export interface ProjectTileProps {
  thumbnailUrl: string;
  icon1Url: string;
  icon2Url: string;
  title: string;
  badge: string;
  type: string;
  stats: ProjectStats;
  isFavorite?: boolean;
  onFavoriteToggle?: () => void;
  onMenuClick?: () => void;
  onProjectClick?: () => void;
}

export function ProjectTile({
  thumbnailUrl,
  icon1Url,
  icon2Url,
  title,
  badge,
  type,
  stats,
  onFavoriteToggle,
  onMenuClick,
  onProjectClick,
}: ProjectTileProps) {
  return (
    <div
      onClick={onProjectClick}
      className="basis-0 border border-solid border-[rgba(11,18,40,0.14)] content-stretch flex flex-col gap-0 grow items-start min-h-px min-w-px relative rounded-[3px] shrink-0 cursor-pointer hover:shadow-md transition-shadow"
    >
      {/* Project Thumbnail */}
      <div className="content-stretch flex flex-col gap-[10px] items-start overflow-clip relative shrink-0 w-full">
        <div className="aspect-[475/150] relative shrink-0 w-full">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <img
              alt={title}
              className="absolute h-full left-0 max-w-none top-0 w-full object-cover"
              src={thumbnailUrl}
            />
          </div>
        </div>
      </div>

      {/* Tile Content */}
      <div className="box-border content-stretch flex flex-col gap-[12px] items-start p-[12px] relative rounded-[3px] shrink-0 w-full">
        {/* Icons and Title */}
        <div className="content-stretch flex gap-[16px] h-[40px] items-center relative shrink-0 w-full">
          {/* Icons */}
          <div className="content-stretch flex gap-[6px] items-center relative shrink-0">
            <div className="content-stretch flex items-start relative rounded-[3px] shrink-0">
              <div className="overflow-clip relative shrink-0 size-[32px]">
                <div className="absolute inset-[20.83%_25%] aspect-[76/94]">
                  <img
                    alt=""
                    className="absolute inset-0 max-w-none object-cover pointer-events-none size-full"
                    src={icon1Url}
                  />
                </div>
              </div>
            </div>
            <div className="content-stretch flex items-center justify-center relative rounded-[3px] shrink-0">
              <div className="overflow-clip relative shrink-0 size-[32px]">
                <div className="absolute aspect-[709/181] left-[16.67%] right-[16.67%] top-1/2 translate-y-[-50%]">
                  <img
                    alt=""
                    className="absolute inset-0 max-w-none object-50%-50% object-cover pointer-events-none size-full"
                    src={icon2Url}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Main Space */}
          <div className="basis-0 content-stretch flex flex-col gap-0 grow items-start justify-center min-h-px min-w-px relative shrink-0">
            {/* Text and Buttons */}
            <div className="content-stretch flex items-center justify-between relative shrink-0 w-full">
              {/* Text */}
              <div className="content-stretch flex gap-[4px] items-center justify-center relative shrink-0">
                <p
                  className="font-['Noto_Sans:Regular',sans-serif] font-normal leading-[20px] relative shrink-0 text-[#292a2e] text-[14px] text-nowrap whitespace-pre"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  {title}
                </p>
                <div className="box-border content-stretch flex items-center px-[6px] py-0 relative rounded-[3px] shrink-0 bg-[rgba(5,21,36,0.06)]">
                  <p
                    className="font-['SF_Pro:Regular',sans-serif] font-normal leading-[16px] relative shrink-0 text-[#292a2e] text-[12px] text-center text-nowrap whitespace-pre"
                    style={{ fontVariationSettings: "'wdth' 100" }}
                  >
                    {badge}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="content-stretch flex gap-[4px] items-center relative shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onFavoriteToggle?.();
                  }}
                  className="content-stretch flex items-start relative rounded-[3px] shrink-0 cursor-pointer hover:bg-[rgba(5,21,36,0.06)] p-1 transition-colors"
                >
                  <div className="overflow-clip relative shrink-0 size-[24px]">
                    <StarIcon className="block size-full text-[#292A2E]" />
                  </div>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMenuClick?.();
                  }}
                  className="content-stretch flex items-start relative rounded-[3px] shrink-0 cursor-pointer hover:bg-[rgba(5,21,36,0.06)] p-1 transition-colors"
                >
                  <div className="overflow-clip relative shrink-0 size-[24px]">
                    <DotsMenuIcon className="block size-full text-[#292A2E]" />
                  </div>
                </button>
              </div>
            </div>

            {/* Type */}
            <div className="content-stretch flex gap-[4px] items-center justify-center relative shrink-0">
              <div
                className="flex flex-col font-['SF_Pro:Regular',sans-serif] font-normal justify-center leading-[0] relative shrink-0 text-[#6b6e76] text-[12px] text-nowrap"
                style={{ fontVariationSettings: "'wdth' 100" }}
              >
                <p className="leading-[16px] whitespace-pre">{type}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Specs - Row 1 */}
        <div className="content-stretch flex gap-0 items-start relative shrink-0 w-full">
          {/* Month */}
          <div className="content-stretch flex items-start relative shrink-0">
            <div className="content-stretch flex gap-[10px] items-center relative shrink-0 w-[225.5px]">
              <div className="content-stretch flex items-start relative shrink-0">
                <div className="overflow-clip relative shrink-0 size-[24px]">
                  <CalendarIcon className="block size-full text-[#292A2E]" />
                </div>
              </div>
              <div className="content-stretch flex flex-col gap-[2px] items-start leading-[0] relative shrink-0 text-center text-nowrap">
                <div
                  className="flex flex-col font-['Noto_Sans:Bold',sans-serif] font-bold justify-center relative shrink-0 text-[#292a2e] text-[12px]"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[16px] text-nowrap whitespace-pre">
                    Month
                  </p>
                </div>
                <div
                  className="flex flex-col font-['Noto_Sans:Medium',sans-serif] font-medium justify-center relative shrink-0 text-[#292a2e] text-[14px]"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[20px] text-nowrap whitespace-pre">
                    {stats.month}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="content-stretch flex items-start relative shrink-0">
            <div className="content-stretch flex gap-[10px] items-center relative shrink-0 w-[225.5px]">
              <div className="content-stretch flex items-start relative shrink-0">
                <div className="overflow-clip relative shrink-0 size-[24px]">
                  <TagIcon className="block size-full text-[#292A2E]" />
                </div>
              </div>
              <div className="content-stretch flex flex-col gap-[2px] items-start leading-[0] relative shrink-0 text-center text-nowrap">
                <div
                  className="flex flex-col font-['Noto_Sans:Bold',sans-serif] font-bold justify-center relative shrink-0 text-[#292a2e] text-[12px]"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[16px] text-nowrap whitespace-pre">
                    Tags
                  </p>
                </div>
                <div
                  className="flex flex-col font-['Noto_Sans:Medium',sans-serif] font-medium justify-center relative shrink-0 text-[#292a2e] text-[14px]"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[20px] text-nowrap whitespace-pre">
                    {stats.tags.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Specs - Row 2 */}
        <div className="content-stretch flex gap-0 items-start relative shrink-0 w-full">
          {/* Next Milestone */}
          <div className="content-stretch flex items-start relative shrink-0">
            <div className="content-stretch flex gap-[10px] items-center relative shrink-0 w-[225.5px]">
              <div className="content-stretch flex items-start relative shrink-0">
                <div className="overflow-clip relative shrink-0 size-[24px]">
                  <FlagIcon className="block size-full text-[#292A2E]" />
                </div>
              </div>
              <div className="content-stretch flex flex-col gap-[2px] items-start leading-[0] relative shrink-0 text-center text-nowrap">
                <div
                  className="flex flex-col font-['Noto_Sans:Bold',sans-serif] font-bold justify-center relative shrink-0 text-[#292a2e] text-[12px]"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[16px] text-nowrap whitespace-pre">
                    Next milestone
                  </p>
                </div>
                <div
                  className="flex flex-col font-['Noto_Sans:Medium',sans-serif] font-medium justify-center relative shrink-0 text-[#292a2e] text-[14px]"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[20px] text-nowrap whitespace-pre">
                    {stats.nextMilestone}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
